import { cn } from '@/utils/styles';
import {
  getBoostersAndBlockersImageUrl,
  getFoodImageUrl
} from '@utils/nutrition/getImageUrl';
import { Image } from '@design-system/Image';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@viome/viome-base/src/usecase/Home/Home.model';
import { getFormulaImageUrl } from '@utils/nutrition/getFormulaImageUrl';

interface BoosterBlockerCardProps {
  badgeItem: Badge;
  mood: string;
  influencer: 'booster' | 'blocker';
  className?: string;
  'data-testid'?: string;
}

export const BoosterBlockerCard = ({
  badgeItem,
  influencer,
  className,
  'data-testid': testId,
}: BoosterBlockerCardProps) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate('/boosters-and-blockers/detail', {
      state: {
        badgeItem,
        influencer
      }
    });
  }

  const displayItems = badgeItem?.items?.length > 3 ? badgeItem.items.slice(0, 2) : badgeItem?.items;
  const remainingCount = badgeItem?.items?.length > 3 ? badgeItem.items.length - 2 : 0;

  return (
    <div
      className={cn(
        'w-[164px] min-w-[115px] min-h-28',
        'rounded-md',
        'bg-white',
        'p-4',
        'flex flex-col',
        'justify-between items-center',
        'transition-shadow',
        'cursor-pointer',
        'hover:shadow-md',
        className,
      )}
      onClick={handleClick}
      data-testid={testId}
    >
      <div className="flex flex-col justify-between" data-testid="boosterBlockerCard-content">
        <div className='flex flex-col items-center' data-testid="boosterBlockerCard-header">
          {badgeItem?.badge_name && (
            <img
              src={getBoostersAndBlockersImageUrl(badgeItem.badge_icon)}
              alt={badgeItem.badge_name}
              className="w-16 h-16 rounded-full items-center"
              data-testid="boosterBlockerCard-badgeIcon"
            />
          )}
          <span className="typography-btn-medium text-secondary" data-testid="boosterBlockerCard-badgeName">
            {badgeItem.badge_name}
          </span>
        </div>

        <div className="px-5 pb-3 pt-5 flex flex-row [&>:not(:first-child)]:-ml-6" data-testid="boosterBlockerCard-itemsContainer">
          {displayItems?.map((item, index) => (
            <div
              key={`${item.item_image}-${index}`}
              className="rounded-full border border-stroke-secondary-subtle backdrop-blur-[12.45px] overflow-hidden size-16 flex justify-center items-center"
              data-testid={`boosterBlockerCard-item-${index}`}
            >
              <Image
                width={44}
                height={44}
                aria-hidden
                src={
                  item.item_type === 'FOOD'
                    ? getFoodImageUrl(item.item_image)
                    : getFormulaImageUrl(item.item_image)
                }
                className="size-11 object-contain"
                data-testid={`boosterBlockerCard-itemImage-${index}`}
              />
            </div>
          ))}

          {remainingCount > 0 && (
            <div
              className="rounded-full border border-stroke-secondary-subtle backdrop-blur-[10.45px] overflow-hidden size-16 flex justify-center items-center"
              data-testid="boosterBlockerCard-remainingCount"
            >
              <span className="typography-btn-medium text-secondary" data-testid="boosterBlockerCard-remainingCountText">
                +{remainingCount}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};